import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import { calibrationService } from "../../../../services/CalibrationService";
import { formatCalibrationDttmUTC, normalizeCalibrationDttm } from "../util";
import StructureMilesBreakdownsDialog from "./StructureMilesBreakdownsDialog";
import {
    extractCalPlatError,
    formatCount,
    formatMiles,
    formatProvenance,
    isStructureMilesStale,
} from "./structureMilesUtils";
import useStructureMiles from "./useStructureMiles";

const STRUCTURE_MILES_TOOLTIP =
    "Structure miles count each physical tower-to-tower span once, so a double-circuit line's shared corridor counts a single time. Circuit miles count every span, so double circuits count twice.";

const UNRESOLVED_TOOLTIP =
    "These spans were skipped because an endpoint structure could not be located in the root-geojson. The totals above exclude them.";

const PROCESS_TYPE = "StructureMilesFromRootGeojson";

// How long to watch for the result after a kickoff before falling back to "check Progress Viewer".
//
// Sized from measurements against dev rather than guessed: a ~5,200-span project took 8.4s from click
// to the dataset being listed, and a ~13,600-span one took past 20s. The lambda's own compute is well
// under a second; the rest is the S3 write and the listing catching up. 25s therefore covers most
// real projects while staying comfortably inside API Gateway's 29s ceiling, so the fallback still
// means "genuinely long-running" rather than "we gave up too early".
//
// Overridable so tests do not have to burn real seconds.
export const DEFAULT_POLL_TIMEOUT_MS = 25000;
export const DEFAULT_POLL_INTERVAL_MS = 1000;

export default function StructureMilesCard({
    client,
    project,
    pollTimeoutMs = DEFAULT_POLL_TIMEOUT_MS,
    pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
}) {
    const { dataset, meta, latestRootGeojsonVersion, loading, loadError, refresh, pollForNewerDataset } =
        useStructureMiles(client, project);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitResult, setSubmitResult] = useState(null);

    // Without this, switching projects mid-request leaves the button stuck reading
    // "Submitting..." for the rest of the old request, and the previous project's status text
    // sitting under the new project's numbers.
    useEffect(() => {
        setSubmitResult(null);
        setSubmitting(false);
    }, [client, project]);

    const handleCalculate = () => {
        setSubmitting(true);
        // Snapshotted so the Progress Viewer link cannot retarget if the user switches projects
        // while the request is still open.
        const submittedFor = { client, project, baselineVersion: meta?.version_dttm ?? null };

        // Shown synchronously, before the request settles, so the click always has an immediate
        // effect even if the run turns out to be slow.
        setSubmitResult({
            kind: "running",
            text: "Calculating structure miles...",
            ...submittedFor,
        });

        // A real failure aborts the poll early rather than making the user wait out the window for a
        // run that never started.
        let failure = null;
        const request = calibrationService.triggerStructureMilesFromRootGeojson(
            client,
            project,
            latestRootGeojsonVersion
        );
        request.catch((error) => {
            failure = error;
            setSubmitResult({
                kind: "failed",
                text: `Failed to submit: ${extractCalPlatError(error)}`,
                ...submittedFor,
            });
        });

        // Polling starts now, in parallel with the request, not after it resolves. The response
        // carries the whole synchronous computation plus the S3 write, so waiting for it before the
        // first check would spend most of the window doing nothing -- and on a gateway timeout it
        // would not begin until 29s in, long after the dataset had actually landed.
        pollForNewerDataset(submittedFor.baselineVersion, {
            timeoutMs: pollTimeoutMs,
            intervalMs: pollIntervalMs,
            shouldAbort: () => failure !== null,
        })
            .then((landed) => {
                // When it lands, the loaded numbers are the confirmation: showSubmitResult stops
                // rendering this message on its own, because the dataset version no longer matches
                // the baseline captured at submit time.
                if (landed || failure) return;
                setSubmitResult({
                    kind: "submitted",
                    text: "Still running. Check Progress Viewer for status, then refresh this card.",
                    ...submittedFor,
                });
            })
            .finally(() => setSubmitting(false));
    };

    const provenance = formatProvenance(meta);
    const isStale = isStructureMilesStale(meta, latestRootGeojsonVersion);
    const unresolved = dataset?.unresolved_span_count;
    const structureMiles = dataset?.total_structure_miles;
    const circuitMiles = dataset?.total_circuit_miles;
    const hasBreakdowns = Boolean(dataset?.by_voltage?.length || dataset?.by_line?.length);
    const canCalculate = Boolean(client && project && latestRootGeojsonVersion) && !submitting;

    // The status copy tells the user to refresh, so it must survive one. It is retired only once
    // a different dataset version is on screen -- at which point the numbers themselves are the
    // confirmation -- or when the client/project changes (handled by the effect above).
    const showSubmitResult =
        submitResult &&
        normalizeCalibrationDttm(meta?.version_dttm ?? null) ===
            normalizeCalibrationDttm(submitResult.baselineVersion);

    const progressViewerUrl = `/calibration/progressViewer?client=${encodeURIComponent(
        submitResult?.client || client || ""
    )}&project=${encodeURIComponent(submitResult?.project || project || "")}&processTypes=${PROCESS_TYPE}`;

    const renderCalculateButton = (label) => (
        <Button variant="outlined" size="small" fullWidth disabled={!canCalculate} onClick={handleCalculate}>
            {submitting ? "Calculating..." : label}
        </Button>
    );

    const renderSubmitStatus = () => {
        if (!showSubmitResult) return null;
        const isRunning = submitResult.kind === "running";
        return (
            <>
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    {isRunning && <CircularProgress size={12} />}
                    <Typography variant="caption" color={submitResult.kind === "failed" ? "error" : "textSecondary"}>
                        {submitResult.text}
                    </Typography>
                </div>
                {/* Only offered once the poll window has expired. Sending someone to the Progress
                    Viewer for a run that is about to finish in under a second would be noise. */}
                {submitResult.kind === "submitted" && (
                    <Link to={progressViewerUrl} target="_blank" rel="noopener noreferrer">
                        <Typography variant="caption">View in Progress Viewer</Typography>
                    </Link>
                )}
            </>
        );
    };

    const renderBody = () => {
        if (!client || !project) {
            return (
                <Typography variant="caption" color="textSecondary">
                    Select a client and project.
                </Typography>
            );
        }

        // Only a first load takes over the body. A refresh with numbers already on screen keeps
        // them rendered and shows progress in the header instead, so an in-place reload cannot
        // collapse the card and yank the controls below it up the sidebar.
        if (loading && !dataset && !loadError) {
            return <CircularProgress size={20} />;
        }

        // Deliberately never the missing-dataset copy: a transient read failure must not assert
        // the dataset is absent and offer to recompute something that may already exist.
        if (loadError) {
            return (
                <>
                    <Typography variant="body2" color="error">
                        Could not load structure miles.
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                        {extractCalPlatError(loadError)}
                    </Typography>
                    <Button variant="text" size="small" onClick={refresh}>
                        Retry
                    </Button>
                </>
            );
        }

        if (!dataset) {
            return (
                <>
                    <Typography variant="body2" color="textSecondary">
                        No structure miles for this project yet.
                    </Typography>
                    {latestRootGeojsonVersion ? (
                        <Typography variant="caption" color="textSecondary">
                            Will compute from root-geojson {formatCalibrationDttmUTC(latestRootGeojsonVersion)}.
                        </Typography>
                    ) : (
                        <Typography variant="caption" color="textSecondary">
                            No root-geojson found for this project. Build a RootGeojson first.
                        </Typography>
                    )}
                    {renderCalculateButton("Calculate")}
                    {renderSubmitStatus()}
                </>
            );
        }

        return (
            <>
                <div style={{ display: "flex", flexDirection: "row", gap: "8px" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" color="textSecondary">
                            Structure
                        </Typography>
                        <Typography variant="h6">{formatMiles(structureMiles)}</Typography>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" color="textSecondary">
                            Circuit
                        </Typography>
                        <Typography variant="h6">{formatMiles(circuitMiles)}</Typography>
                    </div>
                </div>
                <Typography variant="caption" color="textSecondary">
                    miles
                </Typography>

                {/* Guarded because an empty root-geojson legitimately yields 0.0 miles. */}
                {typeof structureMiles === "number" && structureMiles > 0 && typeof circuitMiles === "number" && (
                    <Typography variant="caption" color="textSecondary">
                        {(circuitMiles / structureMiles).toFixed(2)}x circuit / structure
                    </Typography>
                )}

                <Typography variant="caption" color="textSecondary">
                    {formatCount(dataset.unique_span_count)} unique / {formatCount(dataset.total_span_count)} spans
                </Typography>

                {unresolved > 0 && (
                    <Tooltip title={UNRESOLVED_TOOLTIP}>
                        <Typography variant="caption" sx={{ color: "warning.main", cursor: "help" }}>
                            {formatCount(unresolved)} spans unresolved
                        </Typography>
                    </Tooltip>
                )}

                {provenance && (
                    <Typography variant="caption" color="textSecondary" style={{ fontStyle: "italic" }}>
                        {provenance}
                    </Typography>
                )}

                {isStale && (
                    <Chip
                        size="small"
                        color="warning"
                        variant="outlined"
                        label="Newer root-geojson available"
                        style={{ alignSelf: "flex-start" }}
                    />
                )}

                <Button
                    variant="text"
                    size="small"
                    fullWidth
                    disabled={!hasBreakdowns}
                    onClick={() => setDialogOpen(true)}
                >
                    View breakdowns
                </Button>

                {renderCalculateButton("Recalculate")}
                {renderSubmitStatus()}
            </>
        );
    };

    return (
        <>
            {/* The divider lives inside the card so a single component governs both, rather than
                leaving an orphan rule in the sidebar when no project is selected. */}
            <Divider />
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <Typography variant="body2" style={{ fontWeight: "bold" }}>
                        Structure Miles
                    </Typography>
                    <Tooltip title={STRUCTURE_MILES_TOOLTIP}>
                        <InfoOutlinedIcon fontSize="small" style={{ color: "#888", cursor: "help" }} />
                    </Tooltip>
                    <div style={{ flex: 1 }} />
                    {/* Mandatory rather than decorative: the kickoff is fire-and-forget with no
                        polling, so this is the only path from a completed run to fresh numbers. */}
                    {loading && dataset ? (
                        <CircularProgress size={16} />
                    ) : (
                        <IconButton
                            size="small"
                            onClick={refresh}
                            disabled={!client || !project}
                            aria-label="refresh structure miles"
                        >
                            <RefreshIcon fontSize="small" />
                        </IconButton>
                    )}
                </div>
                {renderBody()}
            </div>
            <StructureMilesBreakdownsDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                dataset={dataset}
                meta={meta}
                client={client}
                project={project}
            />
        </>
    );
}
